import { supabase, isSupabaseConfigured } from '../supabaseClient';

/**
 * Push Notifications - Client Side
 * Handles subscribing/unsubscribing to push notifications in the PWA
 */

// VAPID public key (must match the server's VAPID_PRIVATE_KEY)
const VAPID_PUBLIC_KEY = 'BG9LpQKt0A1wJkxyZU67T7OX69XjeMpgIpyxTfUsspa09N569GlG_m-XfuVT9ZZZotMTHi0U-cZwxV1nap95QgA';

/**
 * Convert VAPID key to Uint8Array for push subscription
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Check if push notifications are already subscribed
 */
export async function isPushSubscribed() {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('[Push] Check subscription failed:', error);
    return false;
  }
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    console.warn('[Push] Push notifications not supported');
    return null;
  }

  try {
    // Request permission first
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[Push] Notification permission denied');
      return null;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();

    // Create new subscription if needed
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    }

    console.log('[Push] Subscribed:', subscription.endpoint);
    return subscription;

  } catch (error) {
    console.error('[Push] Subscribe failed:', error);
    return null;
  }
}

/**
 * Save subscription to Supabase (direct, no bot-server needed)
 */
export async function saveSubscription(userId, subscription) {
  if (!isSupabaseConfigured()) {
    console.warn('[Push] Supabase not configured, skipping save');
    return true;
  }

  if (!userId || !subscription) {
    console.warn('[Push] Missing userId or subscription');
    return false;
  }

  try {
    // Safely serialize — toJSON() can throw if the subscription object is stale
    // (e.g., service worker was killed mid-flow on mobile)
    let subJson;
    try {
      subJson = subscription.toJSON();
    } catch (serializationErr) {
      console.error('[Push] Failed to serialize subscription:', serializationErr);
      return false;
    }

    const { error } = await supabase
      .from('user_profiles')
      .update({ push_subscription: subJson })
      .eq('id', userId);

    if (error) throw error;

    console.log('[Push] Subscription saved to Supabase');
    return true;
  } catch (error) {
    console.error('[Push] Save subscription failed:', error);
    return false;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(userId) {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed from browser');
    }

    // Clear subscription from Supabase
    if (userId && isSupabaseConfigured()) {
      await supabase
        .from('user_profiles')
        .update({ push_subscription: null })
        .eq('id', userId);
    }

    return true;
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error);
    return false;
  }
}

/**
 * Initialize push notifications for a user
 * Call this after login
 */
export async function initPushNotifications(userId) {
  if (!isPushSupported()) {
    console.log('[Push] Not supported in this browser');
    return false;
  }

  // Check permission
  if (Notification.permission === 'denied') {
    console.warn('[Push] Notifications blocked by user');
    return false;
  }

  // Subscribe
  const subscription = await subscribeToPush();
  if (!subscription) return false;

  // Save to Supabase
  return await saveSubscription(userId, subscription);
}
